import React, { useEffect, useRef } from 'react';
import { Animated, Easing, StyleSheet, type TextStyle, View } from 'react-native';
import { GradientText } from './GradientText';
import { useReduceMotion } from '../hooks/useReduceMotion';
import { gradients } from '../theme/tokens';

export interface MotDefilantProps {
  mots: string[];
  // Hauteur d'UNE ligne : la fenêtre n'en montre jamais plus.
  hauteur?: number;
  style?: TextStyle;
  testID?: string;
}

// Durée d'un tour complet : 10 s, comme la maquette. Chaque mot reste affiché environ 2,2 s, puis
// glisse en 0,3 s.
const DUREE_TOUR = 10_000;

/**
 * Mot en dégradé qui défile à la verticale, un à la fois, et boucle.
 *
 * Les mots sont EMPILÉS et le PREMIER est RÉPÉTÉ à la fin : au dernier glissement on arrive sur
 * cette copie, puis la valeur repart de zéro sans que l'œil voie un saut.
 *
 * Animations réduites (ou un seul mot) : le premier mot, fixe, rien d'autre dans l'arbre.
 *
 * Accessibilité : le bloc se lit d'un trait avec ses quatre formulations ; la pile, elle, est
 * cachée, sans quoi chaque mot serait lu deux fois.
 */
export function MotDefilant({ mots, hauteur = 44, style, testID }: MotDefilantProps) {
  const reduit = useReduceMotion();
  const anime = !reduit && mots.length > 1;
  const progression = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!anime) return undefined;
    progression.setValue(0);
    const boucle = Animated.loop(
      Animated.timing(progression, {
        toValue: 1,
        duration: DUREE_TOUR,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    );
    boucle.start();
    return () => boucle.stop();
  }, [anime, progression]);

  const affiches = anime ? [...mots, mots[0]] : mots.slice(0, 1);
  const n = mots.length;

  // Chaque mot occupe 1/n du tour : il reste en place les 88 % premiers de sa part, puis glisse
  // d'une ligne. Le dernier glissement amène la copie du premier mot.
  const instants = [0];
  const decalages = [0];
  for (let i = 0; i < n; i += 1) {
    instants.push((i + 0.88) / n, (i + 1) / n);
    decalages.push(i === 0 ? 0 : -i * hauteur, -(i + 1) * hauteur);
  }
  const translation = progression.interpolate({ inputRange: instants, outputRange: decalages });

  return (
    <View
      testID={testID}
      accessible
      accessibilityLabel={mots.join(' ')}
      style={[styles.fenetre, { height: hauteur }]}
    >
      <Animated.View
        testID={testID ? `${testID}-pile` : undefined}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
        style={anime ? { transform: [{ translateY: translation }] } : undefined}
      >
        {affiches.map((mot, i) => (
          <View key={`${mot}-${i}`} style={{ height: hauteur, justifyContent: 'center' }}>
            <GradientText
              colors={gradients.surNuit}
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.7}
              testID={testID ? `${testID}-mot-${i}` : undefined}
              style={{ ...style, lineHeight: hauteur }}
            >
              {mot}
            </GradientText>
          </View>
        ))}
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  fenetre: { overflow: 'hidden' },
});
